import { z } from "zod";

/**
 * Feature flags stored in clients/{clientId}.features.
 * Core flags are available on all tiers; premium flags are subscription-gated.
 * These are read once at app boot and cached for the session.
 */
export const FeatureFlagsSchema = z.object({
  // Core features (included in base subscription)
  reservations: z.boolean().default(false),
  appointments: z.boolean().default(false),
  online_ordering: z.boolean().default(false),
  quote_forms: z.boolean().default(false),

  // Add-ons (activated per subscription item)
  sms_reminders: z.boolean().default(false),
  review_engine: z.boolean().default(false),
  cms: z.boolean().default(false),
  wholesale_portal: z.boolean().default(false),
  private_events: z.boolean().default(false),
});

export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/**
 * The core client document stored at clients/{clientId}.
 * Public-readable (no secrets — those live in clients/{clientId}/private/stripe).
 */
export const ClientConfigSchema = z.object({
  clientId: z.string(),
  businessName: z.string(),
  vertical: z.enum(["food-bev", "service", "trade"]),
  layoutType: z.string().optional(), // deprecated — vertical is the canonical field
  status: z.enum(["active", "suspended", "trial"]).default("trial"),
  features: FeatureFlagsSchema.default({}),
  // Subscription status mirror (safe public fields only — IDs live in private/stripe)
  subscription: z
    .object({
      interval: z.enum(["monthly", "yearly"]).optional(),
      status: z
        .enum(["active", "past_due", "canceled", "incomplete", "incomplete_expired", "trialing", "unpaid", "paused"])
        .optional(),
      currentPeriodEnd: z.string().nullish(),
      addOns: z.array(z.string()).optional(),
    })
    .optional(),
  // Stripe Connect onboarding status mirror (NOT the account id — that's server-only)
  stripe: z
    .object({
      chargesEnabled: z.boolean().default(false),
      onboarded: z.boolean().default(false),
    })
    .optional(),
  contact: z
    .object({
      phone: z.string().nullish(),
      address: z.string().nullish(),
      email: z.string().nullish(),
    })
    .optional(),
  // Per-add-on lifecycle: requested by client → available (Ryan enabled) → active (subscribed)
  addonRequests: z
    .record(
      z.object({
        status: z.enum(["requested", "available", "active"]),
        requestedAt: z.string(),
        availableAt: z.string().optional(),
        activatedAt: z.string().optional(),
      }),
    )
    .optional(),
  analytics: z
    .object({
      measurementId: z.string().optional(),
      // GA4 dataStream numeric ID — used to filter the GA4 Data API by stream
      // instead of by display name, which drifts from businessName over time.
      streamId: z.string().optional(),
      // This client's own registered Firebase Web App ID. Required for gtag hits to
      // actually send — the Firebase Analytics SDK resolves the *authoritative*
      // measurementId server-side from appId, ignoring any measurementId passed
      // locally, so each client needs its own Web App (not just a distinct
      // measurementId string) to report to its own GA4 stream.
      appId: z.string().optional(),
    })
    .optional(),
  jobberConnected: z.boolean().optional(),
  updatedAt: z.string().optional(),
});

export type ClientConfig = z.infer<typeof ClientConfigSchema>;
